/**
 * What a file frame offers where it is shown (plan 14). A file linked on
 * this computer opens in its default app, shows in Finder or Explorer, and
 * once found missing offers Locate…; where this computer has no link for
 * the frame, Link a file… (denied: Allow in Settings). A file
 * linked on another computer, or seen on the web, offers nothing to open and
 * says where it is. A file at an https URL (an agent's) opens in the
 * browser; one of our own uploads has no Open (the server sends it as an
 * attachment: Download saves it).
 */
import { openUrl } from "@tauri-apps/plugin-opener";
import { useMemo } from "react";
import { useT } from "@/i18n";
import { framesFileMessages } from "@/i18n/messages/frames-file";
import type { FrameData } from "@/lib/frame-data";
import { isTauri } from "@/lib/is-tauri";
import {
  linkPlace,
  localLink,
  useThisComputer,
  type LocalLink,
} from "@/lib/local-file-link";
import { isWindowsPlatform } from "@/lib/os-platform";
import { pdfSource, type PdfSource } from "@/lib/pdf-document";
import { safeWebUrl } from "@/lib/safe-web-url";
import { ownUploadUrl } from "@/lib/upload-url";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useProjectStore } from "@/stores/project-store";
import {
  useLinkProblem,
  type LinkProblem,
} from "@/stores/local-file-problem-store";
import {
  allowFolderAccess,
  locateLinkedFile,
  openLinkedFile,
  revealLinkedFile,
} from "./linked-file-actions";

/** What a file frame says instead of its file. */
export type FileNotice =
  | { kind: "elsewhere"; computer: string }
  | { kind: LinkProblem };

export interface FileActions {
  /** Opens the file; null when nothing opens it here. */
  onOpen: (() => void) | null;
  /** Shows a file linked here in Finder or Explorer. */
  onReveal: (() => void) | null;
  /** Locate… a file linked here that was found missing. */
  onLocate: (() => void) | null;
  /** Link a file… where this computer has no link for the frame. */
  onLinkFile: (() => void) | null;
  /** Lets Denker into the folder macOS kept it from. */
  onAllow: (() => void) | null;
  notice: FileNotice | null;
  /** The source pdf.js reads a PDF from, null when nothing here may. */
  pdf: PdfSource | null;
  /** True while the desktop app says which computer this is. */
  pending: boolean;
}

/**
 * Open an https file in the OS browser on desktop (never as a local file:
 * a downloaded file is not run), a new tab on the web.
 */
async function openWebFile(url: string): Promise<void> {
  if (isTauri()) {
    try {
      await openUrl(url);
      return;
    } catch {
      /* fall through to browser open */
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Run `act`, which brings another app or Finder forward, after closing the
 * workspace: it covers the screen, so what opens would stay hidden behind
 * it. Nothing changes outside the workspace.
 */
function outsideWorkspace(act: () => Promise<void>): () => void {
  return () => {
    useDesktopMode.getState().closeWorkspace();
    void act();
  };
}

/** "Show in Finder" or "Show in Explorer". */
export function useRevealLabel(): string {
  const t = useT(framesFileMessages);
  return isWindowsPlatform() ? t("showInExplorer") : t("showInFinder");
}

/**
 * What a file's card adds to its type and size: "On <computer>" for a file
 * linked elsewhere; "File not found" or "Not linked on this computer" once
 * this window found so.
 */
export function useFileNote(source: Record<string, unknown>): string | null {
  const t = useT(framesFileMessages);
  const link = localLink(source);
  const place = useLinkPlace(link);
  const problem = useLinkProblem(link?.linkId ?? null);
  if (!link) return null;
  if (place === "elsewhere") {
    return t("onComputer", { name: link.machineName || t("anotherComputer") });
  }
  if (place !== "here") return null;
  if (problem === "missing") return t("fileNotFound");
  return problem === "unlinked" ? t("notLinkedHere") : null;
}

/** Where `link` is from this window (`linkPlace`). */
function useLinkPlace(link: LocalLink | null) {
  const computer = useThisComputer();
  return link ? linkPlace(link, computer) : null;
}

export function useFileActions(data: FrameData): FileActions {
  const { content, frameId, spaceId } = data;
  const link = useMemo(() => localLink(content), [content]);
  const computer = useThisComputer();
  const problem = useLinkProblem(link?.linkId ?? null);
  const place = link ? linkPlace(link, computer) : null;
  // Its own memo: a problem found by reading must not ask for a new read.
  const pdf = useMemo(
    () => pdfSource(content, place === "here" ? frameId : null),
    [content, frameId, place],
  );
  return useMemo<FileActions>(() => {
    const none: FileActions = {
      onOpen: null,
      onReveal: null,
      onLocate: null,
      onLinkFile: null,
      onAllow: null,
      notice: null,
      pdf: null,
      pending: false,
    };
    if (!link) {
      const web =
        ownUploadUrl(content?.url) === null
          ? safeWebUrl(typeof content?.url === "string" ? content.url : "")
          : null;
      return {
        ...none,
        onOpen: web ? outsideWorkspace(() => openWebFile(web)) : null,
        pdf,
      };
    }
    if (place === "pending") return { ...none, pending: true };
    if (place === "elsewhere" || !computer) {
      return {
        ...none,
        notice: { kind: "elsewhere", computer: link.machineName },
      };
    }
    const target = { link, frameId };
    const projectId =
      spaceId ?? useProjectStore.getState().currentProject?.id ?? null;
    const locate = projectId
      ? () => void locateLinkedFile(target, projectId, computer)
      : null;
    const gone = problem === "missing" || problem === "unlinked";
    return {
      ...none,
      onOpen: gone ? null : outsideWorkspace(() => openLinkedFile(target)),
      onReveal: gone ? null : outsideWorkspace(() => revealLinkedFile(target)),
      onLocate: problem === "missing" ? locate : null,
      onLinkFile: problem === "unlinked" ? locate : null,
      onAllow: problem === "denied" ? allowFolderAccess : null,
      notice: problem ? { kind: problem } : null,
      pdf,
    };
  }, [computer, content, frameId, link, pdf, place, problem, spaceId]);
}
